import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import useStore from '../stores';
import { useFeedbackDialogStore } from '../stores/feedbackDialogStore';
import {
  FeedbackPriority,
  useSubmitFeedback,
} from '../hooks/queries/useSubmitFeedback';
import { useErrorStore } from '../stores/errorStore';

const PRIORITY_OPTIONS: {
  value: FeedbackPriority;
  label: string;
  helper: string;
}[] = [
  {
    value: 'P3',
    label: 'Nice to have & general questions',
    helper:
      'Enhancement, minor issue, or general question; no functional break.',
  },
  {
    value: 'P2',
    label: 'Broken, not urgent',
    helper: 'Something is broken but has a workaround or limited impact.',
  },
  {
    value: 'P1',
    label: 'Urgent',
    helper:
      'System-level failure: login, feature nonfunctional, or whole-platform outage.',
  },
];

function FeedbackDialog() {
  const reporterEmail = useStore((state) => state.userProfile.email);
  const open = useFeedbackDialogStore((s) => s.open);
  const setOpen = useFeedbackDialogStore((s) => s.setOpen);

  const [priority, setPriority] = useState<FeedbackPriority>('P3');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const addToast = useErrorStore((s) => s.addToast);
  const env = import.meta.env.VITE_ENV ?? 'unknown';
  const showEnvChip = env !== 'prod';

  const mutation = useSubmitFeedback();

  const reset = () => {
    setPriority('P3');
    setTitle('');
    setDescription('');
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    reset();
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      await mutation.mutateAsync({
        priority,
        title: title.trim(),
        description: description.trim(),
        reporter_email: reporterEmail,
        source_url: window.location.href,
      });
      addToast({
        type: 'success',
        message: 'Thanks - we received your report.',
      });
      reset();
      setOpen(false);
    } catch (e) {
      addToast({
        type: 'error',
        message:
          e instanceof Error
            ? e.message
            : 'Could not submit feedback. Please try again.',
      });
    }
  };

  const canSubmit =
    !mutation.isPending &&
    title.trim().length > 0 &&
    description.trim().length > 0;

  if (!reporterEmail) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>Report an issue</span>
          {showEnvChip && (
            <Chip
              size="small"
              color="warning"
              label={`Environment: gg-${env}`}
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControl>
            <FormLabel id="feedback-priority-label">Priority</FormLabel>
            <RadioGroup
              aria-labelledby="feedback-priority-label"
              value={priority}
              onChange={(e) => setPriority(e.target.value as FeedbackPriority)}
            >
              {PRIORITY_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio />}
                  label={
                    <Stack>
                      <span>{opt.label}</span>
                      <FormHelperText sx={{ m: 0 }}>
                        {opt.helper}
                      </FormHelperText>
                    </Stack>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Title"
            required
            fullWidth
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <TextField
            label="Description"
            required
            fullWidth
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {mutation.isPending ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FeedbackDialog;
